import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import { FaArrowRight, FaFire, FaStar, FaClock, FaCrown, FaLeaf } from 'react-icons/fa';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';
import { isStandalone } from '../utils/isStandalone';
import MobileHeader from '../components/MobileHeader';

const Home = () => {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const standalone = isStandalone();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Only fetch products for the enhanced PWA experience
    if (!standalone) return;

    const fetchProducts = async () => {
      try {
        const response = await axios.get(API_ENDPOINTS.PRODUCTS.LIST);
        setProducts(response.data || []);
      } catch (error) {
        console.error('Error fetching products for PWA home:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [standalone]);

  if (!standalone) {
    return (
      <div className="min-h-screen">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-cigar-dark to-black text-cigar-cream py-12 sm:py-16 md:py-24">
          <video 
            className="absolute inset-0 w-full h-full object-cover opacity-10" 
            autoPlay 
            muted 
            loop
            playsInline
          >
            <source src="/cigarvid.mp4" type="video/mp4" />
          </video>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-cigar font-bold mb-4 sm:mb-6 leading-tight">
              {t('home.title')}
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl mb-6 sm:mb-8 md:mb-10 text-cigar-cream max-w-3xl">
              {t('home.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Link
                to="/products"
                className="bg-gradient-to-r from-cigar-gold to-cigar-bronze hover:from-cigar-bronze hover:to-cigar-gold text-cigar-dark px-6 sm:px-8 py-3 rounded-lg text-base sm:text-lg font-semibold transition-all duration-200 inline-flex items-center justify-center shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                {t('home.shopNow')} <FaArrowRight className={`${isRTL ? 'mr-2' : 'ml-2'}`} />
              </Link>
              <Link
                to="/signup"
                className="border-2 border-cigar-gold text-cigar-gold hover:bg-cigar-gold hover:text-cigar-dark px-6 sm:px-8 py-3 rounded-lg text-base sm:text-lg font-semibold transition-all duration-200 hover:shadow-lg text-center"
              >
                {t('home.learnMore')}
              </Link>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="bg-gradient-to-br from-cigar-brown to-cigar-dark py-8 sm:py-12 md:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8">
              <div className="bg-white rounded-xl shadow-xl p-4 sm:p-6 border border-cigar-light hover:shadow-2xl transition-all duration-300">
                <h3 className="text-lg sm:text-xl font-semibold mb-2 text-cigar-dark">{t('home.premiumQuality')}</h3>
                <p className="text-cigar-charcoal text-sm sm:text-base">{t('home.premiumQualityDesc')}</p>
              </div>
              <div className="bg-white rounded-xl shadow-xl p-4 sm:p-6 border border-cigar-light hover:shadow-2xl transition-all duration-300">
                <h3 className="text-lg sm:text-xl font-semibold mb-2 text-cigar-dark">{t('home.expertCuration')}</h3>
                <p className="text-cigar-charcoal text-sm sm:text-base">{t('home.expertCurationDesc')}</p>
              </div>
              <div className="bg-white rounded-xl shadow-xl p-4 sm:p-6 border border-cigar-light hover:shadow-2xl transition-all duration-300 sm:col-span-2 md:col-span-1">
                <h3 className="text-lg sm:text-xl font-semibold mb-2 text-cigar-dark">{t('home.fastShipping')}</h3>
                <p className="text-cigar-charcoal text-sm sm:text-base">{t('home.fastShippingDesc')}</p>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen pwa-mode bg-gradient-to-b from-black via-[#1a120f] to-black text-cigar-cream">
      <MobileHeader title="CC Cigar" showBack={false} />
      <div className="page-content pb-6">
        {/* Featured Hero */}
        <div className="relative h-56 mb-7 rounded-3xl overflow-hidden shadow-[0_18px_45px_rgba(0,0,0,0.9)] border border-white/10 animate-[fadeIn_0.9s_ease-out]">
          <video 
            className="absolute inset-0 w-full h-full object-cover" 
            autoPlay 
            muted 
            loop
            playsInline
          >
            <source src="/cigarvid.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-transparent flex items-end p-4 sm:p-5">
            <div className="space-y-1.5">
              <h1 className="text-2xl font-bold text-cigar-cream tracking-wide drop-shadow-[0_2px_4px_rgba(0,0,0,0.7)]">
                {t('home.title')}
              </h1>
              <p className="text-xs text-cigar-cream/80 max-w-[280px]">
                {t('home.subtitle')}
              </p>
              <div className="flex gap-2 text-[10px] pt-1">
                <span className="px-2 py-1 rounded-full bg-gradient-to-r from-cigar-gold/30 to-amber-500/40 text-cigar-dark font-semibold shadow-sm border border-amber-200/60">
                  Cuban
                </span>
                <span className="px-2 py-1 rounded-full bg-white/8 text-cigar-cream border border-white/20 backdrop-blur-sm">
                  Non‑Cuban
                </span>
                <span className="px-2 py-1 rounded-full bg-white/8 text-cigar-cream border border-white/20 backdrop-blur-sm">
                  Cigarillos
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-4 mb-7">
          <Link
            to="/products"
            className="bg-gradient-to-br from-cigar-gold/25 via-amber-500/20 to-transparent backdrop-blur-xl border border-cigar-gold/60 rounded-2xl p-4 active:scale-95 transition-all duration-200 shadow-[0_10px_30px_rgba(0,0,0,0.6)] hover:-translate-y-0.5 hover:border-amber-300"
          >
            <FaFire className="text-cigar-dark bg-cigar-gold/90 rounded-full p-2 text-xl mb-2 shadow-md" />
            <h3 className="text-base font-semibold text-cigar-cream tracking-wide">{t('home.shopNow')}</h3>
            <p className="text-xs text-cigar-cream/70">{t('home.featuredProducts')}</p>
          </Link>
          <Link
            to="/products"
            className="bg-gradient-to-br from-cigar-bronze/25 via-orange-500/15 to-transparent backdrop-blur-xl border border-cigar-bronze/60 rounded-2xl p-4 active:scale-95 transition-all duration-200 shadow-[0_10px_30px_rgba(0,0,0,0.6)] hover:-translate-y-0.5 hover:border-orange-300"
          >
            <FaStar className="text-cigar-dark bg-cigar-bronze/90 rounded-full p-2 text-xl mb-2 shadow-md" />
            <h3 className="text-base font-semibold text-cigar-cream tracking-wide">{t('home.premiumQuality')}</h3>
            <p className="text-xs text-cigar-cream/70">{t('home.premiumQualityDesc')}</p>
          </Link>
        </div>

        {/* Categorized Rows */}
        <div className="space-y-6">
          <SectionRow
            title="Cuban Classics"
            subtitle="Iconic marcas from Havana"
            icon={<FaCrown className="text-cigar-gold" />}
            products={products.filter(p => p.category === 'Cuban').slice(0, 10)}
            loading={loading}
          />

          <SectionRow
            title="Non‑Cuban Gems"
            subtitle="Boutique blends & modern favorites"
            icon={<FaLeaf className="text-cigar-bronze" />}
            products={products.filter(p => p.category === 'Non-Cuban').slice(0, 10)}
            loading={loading}
          />

          <SectionRow
            title="Cigarillos"
            subtitle="Short smokes, full flavor"
            icon={<FaClock className="text-cigar-cream" />}
            products={products.filter(p => p.category === 'Cigarillo').slice(0, 10)}
            loading={loading}
          />
        </div>
      </div>
    </div>
  );
};

export default Home;

const SectionRow = ({ title, subtitle, icon, products, loading }) => {
  return (
    <div className="animate-[fadeIn_0.6s_ease-out]">
      <div className="flex items-center justify-between mb-2.5 px-1">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center shadow-sm border border-white/15">
            {icon}
          </div>
          <div>
            <h2 className="text-sm font-semibold text-cigar-cream tracking-wide">
              {title}
            </h2>
            <p className="text-[11px] text-cigar-cream/60">{subtitle}</p>
          </div>
        </div>
      </div>
      <div className="overflow-x-auto hide-scrollbar">
        <div className="flex gap-3.5 pb-1.5">
          {loading && (
            <>
              <SkeletonCard />
              <SkeletonCard />
            </>
          )}
          {!loading && products && products.length === 0 && (
            <p className="text-xs text-cigar-cream/50 px-1">
              No cigars in this category yet.
            </p>
          )}
          {!loading && products && products.map((product) => (
            <Link
              to={`/products/${product._id}`}
              key={product._id}
              className="w-40 flex-shrink-0 bg-gradient-to-b from-white/10 to-white/3 border border-white/12 rounded-2xl overflow-hidden shadow-md active:scale-95 transition-transform duration-200 hover:border-cigar-gold/60 hover:shadow-[0_12px_30px_rgba(0,0,0,0.7)]"
            >
              <div className="h-32 w-full bg-black/70">
                <img
                  src={
                    (product.images && product.images[0]) ||
                    product.image ||
                    'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=400&h=600&fit=crop'
                  }
                  alt={product.product_name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-2">
                <h3 className="text-[11px] font-semibold text-cigar-cream line-clamp-2 min-h-[30px]">
                  {product.product_name}
                </h3>
                <p className="text-[10px] text-cigar-cream/60 mb-1 line-clamp-1">
                  {product.brand || product.category}
                </p>
                {product.price_single > 0 && (
                  <p className="text-xs font-bold text-cigar-gold drop-shadow-[0_1px_3px_rgba(0,0,0,0.7)]">
                    {product.currency} {product.price_single}
                  </p>
                )}
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

const SkeletonCard = () => (
  <div className="w-40 flex-shrink-0 rounded-2xl overflow-hidden bg-white/5 border border-white/8 animate-pulse">
    <div className="h-32 w-full bg-gradient-to-br from-white/10 to-white/5" />
    <div className="p-2 space-y-2">
      <div className="h-3 bg-white/12 rounded" />
      <div className="h-3 bg-white/7 rounded w-2/3" />
      <div className="h-3 bg-cigar-gold/30 rounded w-1/2" />
    </div>
  </div>
);
